import './initEnv';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { OidcProvider } from '@axa-fr/react-oidc';
import { ConfigureFlopFlip } from '@flopflip/react-redux';
import { Helmet, HelmetProvider } from 'react-helmet-async';
// Import the webpack polyfills first
import './webpack/polyfills';
import './index.css';
import './style.scss';
import './keyboardModality';
import App from './App';
import * as serviceWorker from './serviceWorker';
import store from './store/configureStore';
import ErrorBoundary from './ErrorBoundary';
import { ServiceProvider } from './context/ServiceContext';
import { oidcConfiguration } from './components/auth/AuthConfig';
import ClockSkewPreflight from './components/auth/ClockSkewPreflight';
import adapter from './store/flopflipAdapter';
import logger from './utils/logger';
import LoadingModal from './components/modal/LoadingModal';
import defaultFeatureFlags from './store/defaultFeatureFlags';
// Conditionally install a console limiter in `info` mode to reduce noise.
const runtimeLogLevel = (
  (typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_LOG_LEVEL)
  || 'info'
);
if (runtimeLogLevel === 'info') {
  try { require('./initConsoleLimiter'); } catch (e) { /* ignore */ }
}

// eslint-disable-next-line no-underscore-dangle
const {
  REACT_APP_AEC_LAUNCH_SCRIPT_URL,
} = window.__ENV || {};
const adminPage = window.location.pathname.toLowerCase().includes('admin');
const AEC_LAUNCH_URL_FLAG_CHECKED = [REACT_APP_AEC_LAUNCH_SCRIPT_URL];

const overrideDefaults = () => null;

const oidcProps = {
  configuration: oidcConfiguration,
  loadingComponent: LoadingModal,
  authenticatingComponent: LoadingModal,
  callbackSuccessComponent: overrideDefaults,
  sessionLostComponent: overrideDefaults,
  onError: error => {
    logger.error('OIDC Error:', error);
    // Redirect to home on error to break loop
    if (window.location.pathname.includes('/oidc/callback')) {
      window.location.replace(`${window.location.origin}/application/quick-app`);
    }
  },
  onLogout: () => {
    // Clear any auth-related storage to ensure clean state
    sessionStorage.removeItem('returnUrl');
  },
};

const root = createRoot(document.getElementById('root'));

root.render(
  <Provider store={store}>
    <ConfigureFlopFlip
      adapter={adapter}
      adapterArgs={{
        user: { id: 'anonymous' },
        flags: defaultFeatureFlags,
      }}
    >
      <ClockSkewPreflight includeProtectedPaths={false}>
        <OidcProvider {...oidcProps}>
          <ServiceProvider>
            <HelmetProvider>
              <>
                {!adminPage && (
                  <Helmet script={
                    [...AEC_LAUNCH_URL_FLAG_CHECKED].map(script => ({
                      src: script,
                      type: 'text/javascript',
                      defer: true,
                      async: true,
                    }))
                  }
                  />
                )}
                <React.StrictMode>
                  <ErrorBoundary>
                    <App featureToggles={defaultFeatureFlags} />
                  </ErrorBoundary>
                </React.StrictMode>
              </>
            </HelmetProvider>
          </ServiceProvider>
        </OidcProvider>
      </ClockSkewPreflight>
    </ConfigureFlopFlip>
  </Provider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
